import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { MapPin, Phone, Search, ArrowRight } from 'lucide-react';
import { SEO } from '../SEO';
import { ReviewHighlightsSection } from '../ReviewHighlightsSection';
import { LOCATIONS, isFullServiceLocation } from '../locations/locationData';
import {
  FEATURED_SERVICE_AREA_SLUGS,
  HUB_SERVICE_AREA_STRINGS,
  getLocationLandingPath,
} from './locationSeoData';

export const ServiceAreasPage = () => {
  const [query, setQuery] = useState('');
  const indianaLocations = useMemo(
    () => LOCATIONS.filter((location) => location.stateAbbr === 'IN'),
    []
  );
  const featuredLocations = useMemo(
    () => FEATURED_SERVICE_AREA_SLUGS
      .map((slug) => LOCATIONS.find((location) => location.slug === slug))
      .filter(Boolean),
    []
  );
  const serviceAreaSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'ServiceArea',
      name: 'Swift Move and Clean Service Areas',
      description: 'Professional moving and cleaning services across 39+ Indiana communities',
      areaServed: HUB_SERVICE_AREA_STRINGS,
      provider: {
        '@type': 'MovingCompany',
        name: 'Swift Move and Clean',
        telephone: '+18126694165',
        url: 'https://www.swiftmoveandclean.com',
      },
    }),
    []
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return LOCATIONS;
    return LOCATIONS.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        (l.neighborhoods || []).some((n) => n.toLowerCase().includes(q)) ||
        (l.zipCodes || []).some((z) => z.includes(q))
    );
  }, [query]);

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="service-areas-page">
      <SEO
        title="Indiana Service Areas | Movers & Cleaners in 39+ Cities | Swift Move and Clean"
        description="Swift Move and Clean provides licensed movers and eco-friendly cleaners across 39+ Indiana communities including Columbus, Bloomington, Indianapolis, Carmel, and Fishers. Free quotes. No travel fees in Bartholomew County."
        keywords="Indiana service areas, Columbus Bloomington Indianapolis Carmel Fishers movers, Indiana moving company service area, eco-friendly cleaners Indiana cities"
        canonical="/service-areas"
        schema={[serviceAreaSchema]}
      />

      {/* Hero */}
      <section
        className="text-white py-20"
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 45%, #0891b2 100%)'
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/15 px-4 py-2 rounded-full mb-5 backdrop-blur-sm">
              <MapPin className="h-4 w-4" />
              <span className="text-sm font-medium">Indiana Service Areas</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight">
              Trusted Movers and Cleaners Across {indianaLocations.length}+ Indiana Cities
            </h1>
            <p className="text-lg sm:text-xl text-white/90 leading-relaxed">
              From our Columbus headquarters we serve families and businesses across South Central
              Indiana and the Indianapolis metro. Moving services reach up to 2 hours out. Recurring
              cleaning plans focus on communities within 30 minutes of Columbus. Find your city below
              for local details, pricing, and a free quote.
            </p>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-10">
        <div className="max-w-4xl mx-auto text-gray-700 text-lg leading-relaxed">
          <p>
            We serve{' '}
            {featuredLocations.map((location, index) => {
              const isLast = index === featuredLocations.length - 1;
              const isSecondToLast = index === featuredLocations.length - 2;

              return (
                <React.Fragment key={location.slug}>
                  <Link to={getLocationLandingPath(location.slug)} className="font-semibold text-sky-blue hover:underline">
                    {location.name}
                  </Link>
                  {isLast ? '' : isSecondToLast ? ', and ' : ', '}
                </React.Fragment>
              );
            })}
            {' '}plus 30+ other Indiana communities.
          </p>
        </div>
      </section>

      {/* Search */}
      <section className="container mx-auto px-4 pb-12">
        <div className="max-w-xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by city, neighborhood, or zip code..."
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-transparent text-base shadow-sm"
              data-testid="service-areas-search"
            />
          </div>
        </div>
      </section>

      {/* City Grid */}
      <section className="container mx-auto px-4 pb-20">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            No matching service areas. Call{' '}
            <a href="tel:8126694165" className="text-sky-blue font-semibold">
              (812) 669-4165
            </a>{' '}
            — we may still be able to help.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="service-areas-grid">
            {filtered.map((loc) => {
              const fullService = isFullServiceLocation(loc.slug);
              return (
              <Link
                key={loc.slug}
                to={getLocationLandingPath(loc.slug)}
                className="group"
                data-testid={`service-area-card-${loc.slug}`}
              >
                <Card className="h-full border-0 shadow-md hover:shadow-xl transition-all group-hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="h-10 w-10 rounded-lg bg-sky-50 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-sky-600" />
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {loc.zipCodes[0]}
                      </Badge>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-sky-blue transition-colors">
                      {loc.name}, {loc.stateAbbr}
                    </h3>
                    <p className="text-xs text-gray-500 mb-2">
                      Population {loc.population}
                    </p>
                    <Badge
                      className={
                        fullService
                          ? 'mb-3 bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                          : 'mb-3 bg-sky-100 text-sky-700 hover:bg-sky-100'
                      }
                      data-testid={`service-focus-badge-${loc.slug}`}
                    >
                      {fullService ? 'Moving + Cleaning' : 'Moving'}
                    </Badge>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                      {loc.intro}
                    </p>
                    <div className="flex items-center text-sky-blue text-sm font-semibold">
                      View details
                      <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
              );
            })}
          </div>
        )}
      </section>

      <ReviewHighlightsSection
        title="What Recent Customers Said"
        subtitle="These recent reviews give you a quick feel for how Swift Move and Clean handles last-minute jobs, packing help, and professional service across our service areas."
        className="py-16 bg-gradient-to-br from-gray-50 to-blue-50"
      />

      {/* CTA */}
      <section
        className="text-white py-16"
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 60%, #0891b2 100%)'
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Don't See Your City?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            We regularly service additional Indiana communities on a case-by-case basis. Call us —
            we&apos;ll often say yes.
          </p>
          <Button
            size="lg"
            className="bg-bright-orange hover:bg-orange-600 text-white"
            onClick={() => (window.location.href = 'tel:8126694165')}
            data-testid="service-areas-cta-call"
          >
            <Phone className="h-5 w-5 mr-2" />
            Call (812) 669-4165
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ServiceAreasPage;
